import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import CashManagementContainer from './container';
import {
  useGetRunningShiftsQuery,
  useGetStaffListQuery,
  useGetCashHistoryQuery,
  useGetCashReportsQuery,
  useGetWalletBalanceQuery,
  useGetWalletTransactionsQuery,
} from './services';

const CashManagement = () => {
  const token = useSelector((state) => state?.posData?.token);

  // Active Tab state: 'current_shift' | 'history' | 'reports' | 'transactions'
  const [activeTab, setActiveTab] = useState('current_shift');
  const [previousTab, setPreviousTab] = useState('current_shift');

  // Tab 4: Wallet Transactions State (Screen view)
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [transactionDirection, setTransactionDirection] = useState('all');
  const [transactionDateRange, setTransactionDateRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });
  const [transactionPaginationModel, setTransactionPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Tab 1: Current Shift State
  const [runningSearch, setRunningSearch] = useState('');
  const [runningPaginationModel, setRunningPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Tab 2: Cash History State
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatus, setHistoryStatus] = useState('all');
  const [historyStaff, setHistoryStaff] = useState('all');
  const [historyDateRange, setHistoryDateRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });
  const [historyPaginationModel, setHistoryPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Tab 3: Reports State
  const [reportsStaff, setReportsStaff] = useState('all');
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });

  // Modal State for Shift Details / Print Receipt
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoPrint, setAutoPrint] = useState(false);

  // Queries
  // 1. Staff dropdown options
  const { data: staffListResp } = useGetStaffListQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const staffOptions = staffListResp?.data || [];

  // 2. Tab 1 Running Shifts
  const {
    data: runningResp,
    isLoading: runningLoading,
  } = useGetRunningShiftsQuery(
    {
      page: runningPaginationModel.page + 1,
      limit: runningPaginationModel.pageSize,
      search: runningSearch,
    },
    {
      skip: activeTab !== 'current_shift',
      refetchOnMountOrArgChange: true,
    }
  );
  const runningShifts = runningResp?.data?.records || [];
  const runningTotalCount =
    runningResp?.data?.totalCount ??
    runningResp?.data?.pagination?.total ??
    runningShifts.length;

  // 3. Tab 2 Shift History
  const historyStartDateStr = historyDateRange?.startDate
    ? dayjs(historyDateRange.startDate).format('YYYY-MM-DD')
    : '';
  const historyEndDateStr = historyDateRange?.endDate
    ? dayjs(historyDateRange.endDate).format('YYYY-MM-DD')
    : '';

  const {
    data: historyResp,
    isLoading: historyLoading,
  } = useGetCashHistoryQuery(
    {
      status: historyStatus,
      staff_id: historyStaff,
      page: historyPaginationModel.page + 1,
      perPage: historyPaginationModel.pageSize,
      search: historySearch,
      startDate: historyStartDateStr,
      endDate: historyEndDateStr,
    },
    {
      skip: activeTab !== 'history',
      refetchOnMountOrArgChange: true,
    }
  );
  const historyShifts = historyResp?.data?.records || [];
  const historyTotalCount =
    historyResp?.data?.totalCount ??
    historyResp?.data?.pagination?.total ??
    historyShifts.length;

  // 4. Tab 3 Cash Reports
  const reportsStartDateStr = reportsDateRange?.startDate
    ? dayjs(reportsDateRange.startDate).format('YYYY-MM-DD')
    : '';
  const reportsEndDateStr = reportsDateRange?.endDate
    ? dayjs(reportsDateRange.endDate).format('YYYY-MM-DD')
    : '';

  const {
    data: reportsResp,
    isLoading: reportsLoading,
  } = useGetCashReportsQuery(
    {
      staff_id: reportsStaff,
      startDate: reportsStartDateStr,
      endDate: reportsEndDateStr,
    },
    {
      skip: activeTab !== 'reports',
      refetchOnMountOrArgChange: true,
    }
  );
  const reportsData = reportsResp?.data || null;

  // 5. Restaurant Cash Wallet Balance
  const {
    data: walletResp,
    isLoading: walletLoading,
    refetch: refetchWallet,
  } = useGetWalletBalanceQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const walletBalance = walletResp?.data?.balance ?? 0;

  // 6. Tab 4: Wallet Transactions Ledger Query (Screen view)
  const transactionStartDateStr = transactionDateRange?.startDate
    ? dayjs(transactionDateRange.startDate).format('YYYY-MM-DD')
    : '';
  const transactionEndDateStr = transactionDateRange?.endDate
    ? dayjs(transactionDateRange.endDate).format('YYYY-MM-DD')
    : '';

  const {
    data: transactionsResp,
    isLoading: transactionsLoading,
  } = useGetWalletTransactionsQuery(
    {
      page: transactionPaginationModel.page + 1,
      limit: transactionPaginationModel.pageSize,
      type: transactionType,
      direction: transactionDirection,
      search: transactionSearch,
      date_from: transactionStartDateStr,
      date_to: transactionEndDateStr,
    },
    {
      skip: activeTab !== 'transactions',
      refetchOnMountOrArgChange: true,
    }
  );
  const transactionData = transactionsResp?.data || {};
  const transactionsList = transactionData.transactions || [];
  const transactionSummary = transactionData.wallet_summary || {};
  const transactionTotalCount =
    transactionData.pagination?.totalRecords ??
    transactionData.wallet_summary?.total_transactions ??
    transactionsList.length;

  // Export CSV Handler
  const handleExportCsv = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams();
      if (historyStatus) params.append('status', historyStatus);
      if (historyStaff && historyStaff !== 'all') params.append('staff_id', historyStaff);
      if (historySearch && historySearch.trim()) params.append('search', historySearch.trim());
      if (historyStartDateStr) params.append('startDate', historyStartDateStr);
      if (historyEndDateStr) params.append('endDate', historyEndDateStr);

      const url = `${process.env.REACT_APP_API_ROUTE}/pos/cash-management/history/export-csv?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate export file');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `cash-history-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Cash history exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <CashManagementContainer
      // Tabs
      activeTab={activeTab}
      setActiveTab={setActiveTab}

      // Tab 1: Current Shift
      runningShifts={runningShifts}
      runningLoading={runningLoading}
      runningSearch={runningSearch}
      setRunningSearch={setRunningSearch}
      runningPaginationModel={runningPaginationModel}
      setRunningPaginationModel={setRunningPaginationModel}
      runningTotalCount={runningTotalCount}

      // Tab 2: Cash History
      historyShifts={historyShifts}
      historyLoading={historyLoading}
      historySearch={historySearch}
      setHistorySearch={setHistorySearch}
      historyStatus={historyStatus}
      setHistoryStatus={setHistoryStatus}
      historyStaff={historyStaff}
      setHistoryStaff={setHistoryStaff}
      historyDateRange={historyDateRange}
      setHistoryDateRange={setHistoryDateRange}
      historyPaginationModel={historyPaginationModel}
      setHistoryPaginationModel={setHistoryPaginationModel}
      historyTotalCount={historyTotalCount}
      staffOptions={staffOptions}
      handleExportCsv={handleExportCsv}
      exportLoading={exportLoading}

      // Tab 3: Reports
      reportsData={reportsData}
      reportsLoading={reportsLoading}
      reportsStaff={reportsStaff}
      setReportsStaff={setReportsStaff}
      reportsDateRange={reportsDateRange}
      setReportsDateRange={setReportsDateRange}

      // Restaurant Cash Wallet
      walletBalance={walletBalance}
      walletLoading={walletLoading}
      refetchWallet={refetchWallet}

      // Tab 4: Wallet Transactions (Screen view)
      transactionSearch={transactionSearch}
      setTransactionSearch={setTransactionSearch}
      transactionType={transactionType}
      setTransactionType={setTransactionType}
      transactionDirection={transactionDirection}
      setTransactionDirection={setTransactionDirection}
      transactionDateRange={transactionDateRange}
      setTransactionDateRange={setTransactionDateRange}
      transactionPaginationModel={transactionPaginationModel}
      setTransactionPaginationModel={setTransactionPaginationModel}
      transactionsList={transactionsList}
      transactionSummary={transactionSummary}
      transactionsLoading={transactionsLoading}
      transactionTotalCount={transactionTotalCount}
      previousTab={previousTab}
      setPreviousTab={setPreviousTab}

      // Modals
      selectedShiftId={selectedShiftId}
      setSelectedShiftId={setSelectedShiftId}
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      autoPrint={autoPrint}
      setAutoPrint={setAutoPrint}
    />
  );
};

export default CashManagement;
